import { useState, useEffect, useRef } from 'react';
import { PESTS } from './data.js';
import { PEST_IMAGES } from './icons.jsx';
import { url } from './url.js';

// ─────────────────────────────────────────────────────────────────────
// PlaagCard — herbruikbare editorial card (full-bleed foto + gradient +
// klikbare card + oranje chip). Gebruikt op:
//   • homepage diensten-grid (Plagen in home-top.jsx)
//   • /diensten pagina-grid (pages.jsx)
//   • /werkgebied/{stad} diensten-grid (stad.jsx)
//   • Carousel hieronder (AndereDienstenCarousel)
// Single source of truth voor de "premium" card-style.
//
//   props.plaag        — { slug, nl, short, from }
//   props.href         — clickable destination
//   props.titleStad    — optional, used for aria-label context
// ─────────────────────────────────────────────────────────────────────
export function PlaagCard({ plaag, href, titleStad }) {
  const photo = PEST_IMAGES[plaag.slug] || null;
  const webp  = photo ? photo.replace(/\.png$/, '.webp') : null;
  return (
    <a
      href={href}
      className="adc-card"
      aria-label={`${plaag.nl} bestrijden${titleStad ? ` in ${titleStad}` : ''}, professionele aanpak`}
    >
      <div className="adc-photo">
        {photo ? (
          <picture>
            <source srcSet={webp} type="image/webp" />
            <img
              src={photo}
              alt={`${plaag.nl} bestrijden, ABC Ongediertebestrijding`}
              loading="lazy"
              decoding="async"
            />
          </picture>
        ) : (
          <div className="adc-photo-fallback"><span>{plaag.nl}</span></div>
        )}
        <span className="adc-chip">
          <span className="adc-chip-dot" aria-hidden="true"></span>
          Gratis offerte
        </span>
        <div className="adc-photo-grad" aria-hidden="true"></div>
      </div>
      <div className="adc-body">
        <h3>{plaag.nl}</h3>
        <p>{plaag.short}</p>
        {/* Tekst-link CTA i.p.v. dikke knop — premium look behouden, maar
            gebruiker ziet duidelijk dat er iets te klikken valt. Hele
            card blijft clickable; deze link is puur visuele cue. */}
        <span className="adc-cta" aria-hidden="true">
          Bekijk aanpak
          <span className="adc-cta-arrow">→</span>
        </span>
      </div>
    </a>
  );
}

// ─────────────────────────────────────────────────────────────────────
// AndereDienstenCarousel — premium herontwerp (2026-05-24).
//
// Design-keuzes (na review: "ziet er een beetje goedkoop uit"):
//   • Full-bleed foto met gradient-overlay onderaan (editorial stijl,
//     vs. ouderwetse "witte card met foto-top + dikke knop")
//   • Hele card is klikbaar, geen aparte CTA-button — voorkomt
//     ecommerce-look. Subtiele pijl rechtsonder schuift bij hover
//   • Foto zoomt subtiel 1.04× bij hover (700ms ease), card zelf
//     blijft stil — geen card-lift (dat is de cheap look)
//   • Arrows zitten BUITEN de cards in de marge, geen ronde knop —
//     dunne chevron in subtiele navy-tint
//   • Progress-bar i.p.v. dots — luxe-signaal (Apple, Linear, Aesop)
//   • Géén autoplay — gebruiker bepaalt tempo
//   • 3 cards zichtbaar + halve peek van card 4 (luxury hint dat er
//     meer is, zonder visuele drukte)
//   • Asymmetrische header: eyebrow + H2 links, "Bekijk alles" rechts
//
//   props.stadSlug    — optional, builds /{stad}/{plaag}bestrijding URLs
//   props.excludeSlug — optional, leaves out one pest (e.g. current page)
//   props.titleStad   — display name for the title (e.g. "Rotterdam")
// ─────────────────────────────────────────────────────────────────────
export function AndereDienstenCarousel({ stadSlug, excludeSlug, titleStad }) {
  const items = PESTS.filter(p => p.slug !== excludeSlug);
  const perSlide = 3;
  const totalSlides = Math.ceil(items.length / perSlide);

  const [slide, setSlide] = useState(0);
  const trackRef = useRef(null);

  const next = () => setSlide(s => Math.min(s + 1, totalSlides - 1));
  const prev = () => setSlide(s => Math.max(s - 1, 0));

  // Build href — if stadSlug provided, link to combo page, else to plaag page
  const linkFor = (p) => stadSlug ? url.stadPlaag(stadSlug, p.slug) : url.plaag(p.slug);

  // Touch/swipe support — premium carousels swipen op mobiel
  const touchRef = useRef({ x: 0, active: false });
  const onTouchStart = (e) => { touchRef.current = { x: e.touches[0].clientX, active: true }; };
  const onTouchEnd = (e) => {
    if (!touchRef.current.active) return;
    const dx = e.changedTouches[0].clientX - touchRef.current.x;
    if (Math.abs(dx) > 50) dx < 0 ? next() : prev();
    touchRef.current.active = false;
  };

  const allDienstenHref = stadSlug ? url.stad(stadSlug) : url.diensten();

  return (
    <section className="adc-section">
      <div className="container">
        {/* Asymmetrische header — eyebrow + H2 links, link rechts */}
        <div className="adc-header">
          <div>
            <div className="adc-eyebrow">
              {titleStad ? `Andere diensten in ${titleStad}` : 'Andere diensten'}
            </div>
            <h2 className="adc-title">
              Of bekijk een <em>andere plaag</em>{titleStad ? <> in {titleStad}</> : null}
            </h2>
          </div>
          <a href={allDienstenHref} className="adc-all-link">
            {stadSlug ? `Alle diensten in ${titleStad}` : 'Alle 15 diensten'}
            <span aria-hidden="true">→</span>
          </a>
        </div>

        {/* Carousel */}
        <div className="adc-carousel">
          <div
            className="adc-viewport"
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            <div
              className="adc-track"
              ref={trackRef}
              style={{ transform: `translateX(-${slide * 100}%)` }}
            >
              {Array.from({ length: totalSlides }).map((_, si) => (
                <div key={si} className="adc-slide">
                  {items.slice(si * perSlide, si * perSlide + perSlide).map(p => (
                    <PlaagCard
                      key={p.slug}
                      plaag={p}
                      href={linkFor(p)}
                      titleStad={titleStad}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* Arrows — buiten de cards, dunne chevron, geen ronde knop */}
          {totalSlides > 1 && (
            <>
              <button
                className="adc-nav adc-nav-prev"
                onClick={prev}
                disabled={slide === 0}
                aria-label="Vorige diensten"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="15 18 9 12 15 6"/>
                </svg>
              </button>
              <button
                className="adc-nav adc-nav-next"
                onClick={next}
                disabled={slide === totalSlides - 1}
                aria-label="Volgende diensten"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              </button>
            </>
          )}
        </div>

        {/* Geen pager/dots — de pijlen + swipe geven al voldoende
            affordance, en de dot-balk verstoorde het rustige beeld.
            (Hint-tekst onder de carousel met "Sleep of klik..." was
            ook overbodig; helemaal weggehaald.) */}
      </div>
    </section>
  );
}
